/**
 * Portable stack: hub cards + panels, and in-drawer modal.
 * Does not touch .side-panel / open-cc.
 */
const JOTFORM_HANDLER_SRC = 'https://cdn.jotfor.ms/s/umd/latest/for-form-embed-handler.js';
const JOTFORM_READY_TIMEOUT = 8000;
const PENDING_SAVE_KEY = 'custom-fit-pending-save';

function loadJotformHandler() {
  if (typeof window.jotformEmbedHandler === 'function') return Promise.resolve();
  const existing = document.querySelector(`script[src="${JOTFORM_HANDLER_SRC}"]`);
  if (existing) {
    if (existing.readyState === 'complete') return Promise.resolve();
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', resolve, { once: true });
      existing.addEventListener('error', reject, { once: true });
    });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = JOTFORM_HANDLER_SRC;
    script.addEventListener('load', resolve, { once: true });
    script.addEventListener('error', reject, { once: true });
    document.head.append(script);
  });
}

function initJotformHandler(iframe) {
  if (!iframe?.id) return Promise.resolve();
  return loadJotformHandler()
    .then(() => {
      window.jotformEmbedHandler?.(`iframe[id="${iframe.id}"]`, 'https://form.jotform.com/');
    })
    .catch(() => {});
}

function revealJotform(form) {
  const iframe = form?.querySelector('.cf-form__iframe');
  if (!form || !iframe) return;
  iframe.dataset.cfReady = 'true';
  form.classList.remove('cf-form--loading');
  form.setAttribute('aria-busy', 'false');
}

window.addEventListener('message', (event) => {
  if (typeof event.data !== 'string') return;
  const args = event.data.split(':');
  if (args[0] !== 'setHeight') return;

  document.querySelectorAll('.cf-form__iframe').forEach((iframe) => {
    if (event.source !== iframe.contentWindow) return;
    if (args[1]) iframe.style.height = `${args[1]}px`;
    const form = iframe.closest('.cf-form');
    if (form?.classList.contains('cf-form--loading')) revealJotform(form);
  });
});

if (!customElements.get('custom-fit-stack')) {
  class CustomFitStack extends HTMLElement {
    connectedCallback() {
      this.modal = this.querySelector('.cf-modal');
      const drawer = this.closest('custom-fit-drawer');
      if (this.modal && drawer) drawer.append(this.modal);

      this.context = {};
      this.readAccount();
      this.onModalClick = this.onModalClick.bind(this);
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('submit', this.onSubmit.bind(this));
      this.modal?.addEventListener('click', this.onModalClick);
      this.restoreSavedFitIfNeeded();
      this.syncAllFlows();
      this.flushPendingSave();
      loadJotformHandler().catch(() => {});
    }

    disconnectedCallback() {
      this.cancelModalClose();
      this.modal?.removeEventListener('click', this.onModalClick);
    }

    setContext(detail) {
      this.context = { ...this.context, ...(detail || {}) };
      this.restoreSavedFitIfNeeded();
      this.syncVariantInputs();
    }

    syncVariantInputs() {
      if (!this.context.variantId) return;
      this.querySelectorAll('.cf-flow__checkout-form').forEach((root) => {
        const idInput = root.querySelector('input[name="id"]');
        if (idInput) idInput.value = this.context.variantId;
      });
    }

    readAccount() {
      let fits = {};
      const savedEl = this.querySelector('[data-custom-fit-saves]');
      try {
        fits = JSON.parse(savedEl?.textContent || '{}') || {};
      } catch {
        fits = {};
      }
      this.account = {
        loggedIn: this.dataset.loggedIn === 'true',
        loginUrl: this.dataset.loginUrl || '/account/login',
        saveUrl: this.dataset.saveUrl || '/apps/custom-fit/save',
        removeUrl: this.dataset.removeUrl || '/apps/custom-fit/remove',
        fits,
      };
    }

    productId() {
      return String(this.context.productId || this.dataset.productId || '');
    }

    savedFitFor(productId) {
      const id = String(productId || '');
      if (!id || !this.account?.fits) return null;
      return this.account.fits[id] || null;
    }

    selectionKey(selections) {
      return Object.keys(selections || {})
        .sort()
        .map((name) => `${name}:${String(selections[name]?.value ?? '').trim()}`)
        .join('|');
    }

    isSavedFitCurrent() {
      const saved = this.savedFitFor(this.productId());
      if (!saved?.selections) return false;
      return this.selectionKey(saved.selections) === this.selectionKey(this.fitPayload().selections);
    }

    checkRadio(name, value) {
      if (!name || value == null) return;
      const raw = String(value);
      const escapedName = CSS.escape(name);
      const match =
        this.querySelector(`input[type="radio"][name="${escapedName}"][value="${CSS.escape(raw)}"]`) ||
        this.querySelector(`input[type="radio"][name="${escapedName}"][value="${CSS.escape(raw.trim())}"]`) ||
        [...this.querySelectorAll(`input[type="radio"][name="${escapedName}"]`)].find(
          (input) => input.value.trim() === raw.trim()
        );
      if (match) match.checked = true;
    }

    restoreSavedFitIfNeeded() {
      if (this.closest('.cf-drawer--editor')) {
        this.syncSaveButtons();
        return;
      }
      const productId = this.productId();
      if (!productId || this._restoredFor === productId) {
        this.syncSaveButtons();
        return;
      }
      const alreadyChosen = Boolean(this.querySelector('.cf-flow__item input[type="radio"]:checked'));
      if (alreadyChosen) {
        this._restoredFor = productId;
        this.syncSaveButtons();
        return;
      }
      const fit = this.savedFitFor(productId);
      if (!fit?.selections) {
        this.syncSaveButtons();
        return;
      }
      Object.entries(fit.selections).forEach(([name, selection]) => {
        this.checkRadio(name, selection?.value);
      });
      this.context = {
        ...this.context,
        productId,
        variantId: fit.variantId || this.context.variantId,
        productHandle: fit.productHandle || this.context.productHandle,
        selections: { ...fit.selections },
      };
      this._restoredFor = productId;
      this.syncAllFlows();
      this.syncSaveButtons();
    }

    cloneFits() {
      try {
        return JSON.parse(JSON.stringify(this.account.fits || {}));
      } catch {
        return { ...(this.account.fits || {}) };
      }
    }

    beginFitWrite() {
      this._fitEpoch = (this._fitEpoch || 0) + 1;
      return this._fitEpoch;
    }

    syncSaveButtons() {
      const saved = this.isSavedFitCurrent();
      this.querySelectorAll('.cf-flow__save-later').forEach((button) => {
        const label = button.querySelector('span') || button;
        label.textContent = saved
          ? button.dataset.removeLabel || 'Remove from your saves'
          : button.dataset.saveLabel || 'Save for later';
        button.setAttribute('aria-pressed', saved ? 'true' : 'false');
      });
    }

    fitPayload() {
      const selections = {};
      this.querySelectorAll('.cf-flow__item').forEach((item) => {
        const checked = item.querySelector('input[type="radio"]:checked');
        if (!checked?.name) return;
        selections[checked.name] = {
          value: checked.value.trim(),
          label: (checked.dataset.fit || '').trim(),
        };
      });
      return {
        productId: this.productId(),
        productHandle: String(this.context.productHandle || this.dataset.productHandle || ''),
        variantId: String(this.context.variantId || this.dataset.variantId || ''),
        selections,
      };
    }

    isFitComplete() {
      const steps = [...this.querySelectorAll('.cf-flow__item')];
      return steps.length > 0 && steps.every((step) => step.querySelector('input[type="radio"]:checked'));
    }

    returnPath() {
      const url = new URL(window.location.href);
      url.searchParams.set('cf_open', '1');
      return `${url.pathname}${url.search}`;
    }

    stripOpenParam() {
      const url = new URL(window.location.href);
      if (!url.searchParams.has('cf_open')) return;
      url.searchParams.delete('cf_open');
      const next = `${url.pathname}${url.search}${url.hash}`;
      window.history.replaceState({}, '', next);
    }

    shouldOpenFromUrl() {
      return new URLSearchParams(window.location.search).get('cf_open') === '1';
    }

    stashPending(payload) {
      try {
        sessionStorage.setItem(PENDING_SAVE_KEY, JSON.stringify(payload));
      } catch {
        /* private mode */
      }
    }

    readPending() {
      try {
        return JSON.parse(sessionStorage.getItem(PENDING_SAVE_KEY) || 'null');
      } catch {
        return null;
      }
    }

    clearPending() {
      try {
        sessionStorage.removeItem(PENDING_SAVE_KEY);
      } catch {
        /* private mode */
      }
    }

    redirectToLogin() {
      const login = new URL(this.account.loginUrl, window.location.origin);
      const returnTo = this.returnPath();
      login.searchParams.set('return_to', returnTo);
      login.searchParams.set('checkout_url', returnTo);
      window.location.assign(`${login.pathname}${login.search}`);
    }

    applyFit(fit) {
      if (!fit?.selections) return;
      Object.entries(fit.selections).forEach(([name, selection]) => {
        this.checkRadio(name, selection?.value);
      });
      this.context = {
        ...this.context,
        productId: fit.productId || this.productId(),
        variantId: fit.variantId || this.context.variantId,
        productHandle: fit.productHandle || this.context.productHandle,
        selections: { ...fit.selections },
      };
      this._restoredFor = this.productId();
      this.syncAllFlows();
      this.syncVariantInputs();
      this.syncSaveButtons();
    }

    openCustomizeDrawer() {
      const detail = {
        productId: this.productId(),
        variantId: this.context.variantId || this.dataset.variantId,
        productHandle: this.context.productHandle || this.dataset.productHandle,
      };
      this.dispatchEvent(new CustomEvent('custom-fit:open', { bubbles: true, detail }));
      const card = this.querySelector('[data-custom-fit="flow"]:not([hidden]) .cf-hub-card');
      window.requestAnimationFrame(() => this.openItem(card));
    }

    accountError(data, status, fallback) {
      const errors = {
        app_not_installed:
          'Could not save your fit. Open custom-fit-saves in the Shopify admin once, then try again.',
        invalid_signature: 'Could not save your fit. App proxy signature failed.',
      };
      return errors[data.error] || data.error || `${fallback} (${status})`;
    }

    async postAccount(url, body) {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify(body),
      });
      const text = await response.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        data = {};
      }
      return { response, data };
    }

    async postSave(payload) {
      const { response, data } = await this.postAccount(this.account.saveUrl, {
        productId: payload.productId,
        productHandle: payload.productHandle,
        variantId: payload.variantId,
        selections: payload.selections,
      });
      if (response.status === 401 || data.error === 'login_required') {
        this.stashPending({ action: 'save', ...payload });
        this.redirectToLogin();
        return { redirected: true };
      }
      if (!response.ok || !data.ok) {
        throw new Error(this.accountError(data, response.status, 'Save failed'));
      }
      return {
        fits: data.fits || {
          ...(this.account.fits || {}),
          [payload.productId]: {
            ...payload,
            savedAt: new Date().toISOString(),
          },
        },
      };
    }

    async postRemove(productId) {
      const { response, data } = await this.postAccount(this.account.removeUrl, { productId });
      if (response.status === 401 || data.error === 'login_required') {
        this.stashPending({ action: 'remove', productId });
        this.redirectToLogin();
        return { redirected: true };
      }
      if (!response.ok || !data.ok) {
        throw new Error(this.accountError(data, response.status, 'Remove failed'));
      }
      const fits = data.fits || { ...(this.account.fits || {}) };
      delete fits[productId];
      return { fits };
    }

    applyFitWrite(epoch, previous, next) {
      if (epoch !== this._fitEpoch) return;
      this.account.fits = next != null ? next : previous;
      this.syncSaveButtons();
    }

    async saveForLater() {
      if (this.closest('.cf-drawer--editor')) return;

      if (this.isSavedFitCurrent()) {
        await this.removeSavedFit();
        return;
      }

      if (!this.isFitComplete()) return;
      await this.commitSave(this.fitPayload());
    }

    async commitSave(payload) {
      if (!payload?.productId || !payload.variantId) return;

      if (!this.account.loggedIn) {
        this.stashPending({ action: 'save', ...payload });
        this.redirectToLogin();
        return;
      }

      const previous = this.cloneFits();
      const epoch = this.beginFitWrite();
      this.account.fits = {
        ...previous,
        [payload.productId]: {
          ...payload,
          savedAt: new Date().toISOString(),
        },
      };
      this.syncSaveButtons();

      try {
        const result = await this.postSave(payload);
        if (result.redirected) return;
        this.applyFitWrite(epoch, previous, result.fits);
      } catch (error) {
        this.applyFitWrite(epoch, previous);
        window.alert(error instanceof Error ? error.message : 'Could not save your fit. Try again.');
      }
    }

    async removeSavedFit() {
      const productId = this.productId();
      if (!productId) return;

      if (!this.account.loggedIn) {
        this.stashPending({ action: 'remove', productId });
        this.redirectToLogin();
        return;
      }

      const previous = this.cloneFits();
      const epoch = this.beginFitWrite();
      const next = this.cloneFits();
      delete next[productId];
      this.account.fits = next;
      this.syncSaveButtons();

      try {
        const result = await this.postRemove(productId);
        if (result.redirected) return;
        this.applyFitWrite(epoch, previous, result.fits);
      } catch (error) {
        this.applyFitWrite(epoch, previous);
        window.alert(error instanceof Error ? error.message : 'Could not remove your fit. Try again.');
      }
    }

    async flushPendingSave() {
      if (this.closest('.cf-drawer--editor')) return;
      const openFromUrl = this.shouldOpenFromUrl();
      if (openFromUrl) this.stripOpenParam();
      const pending = this.readPending();
      const pendingMatches =
        pending?.productId && pending.productId === this.productId();

      if (pendingMatches && this.account.loggedIn) {
        this.clearPending();
        if (pending.action === 'remove') {
          await this.removeSavedFit();
        } else {
          this.applyFit(pending);
          await this.commitSave(this.fitPayload());
        }
        this.openCustomizeDrawer();
        return;
      }

      if (openFromUrl) this.openCustomizeDrawer();
    }

    onChange(event) {
      const input = event.target;
      if (!input.matches('.cf-flow__button input[type="radio"]')) return;

      const alreadyChosen = Boolean(this.context.selections?.[input.name]);
      this.context = {
        ...this.context,
        selections: {
          ...(this.context.selections || {}),
          [input.name]: { value: input.value.trim(), label: (input.dataset.fit || '').trim() },
        },
      };
      this.syncSaveButtons();

      const step = input.closest('.cf-flow__item');
      const flow = input.closest('.cf-flow');
      if (!step || !flow) return;

      this.syncFlow(flow);
      if (alreadyChosen) return;

      const steps = [...flow.querySelectorAll('.cf-flow__item')];
      const next = steps[steps.indexOf(step) + 1] || flow.querySelector('.cf-flow__checkout');
      const scroller = next?.closest('.cf-hub-panel__body');
      if (next && scroller && next.classList.contains('cf-flow__checkout')) {
        const top =
          scroller.scrollTop + next.getBoundingClientRect().top - scroller.getBoundingClientRect().top + 26;
        scroller.scrollTo({ top, behavior: 'smooth' });
        return;
      }
      next?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    onSubmit(event) {
      const form = event.target;
      if (!form.classList?.contains('cf-flow__checkout-form')) return;
      event.preventDefault();
      this.checkout(form);
    }

    onClick(event) {
      const saveLater = event.target.closest('.cf-flow__save-later');
      if (saveLater) {
        event.preventDefault();
        this.saveForLater(saveLater);
        return;
      }

      const card = event.target.closest('.cf-hub-card');
      if (card) {
        this.openItem(card);
        return;
      }

      if (event.target.closest('.cf-hub-panel__back')) {
        this.closePanels();
        return;
      }

      if (event.target.closest('.cf-flow__modal-trigger')) {
        event.stopPropagation();
        this.openModal(event.target.closest('.cf-flow__item'));
      }
    }

    onModalClick(event) {
      if (Date.now() < this._modalClickGuard) return;
      if (
        event.target.closest('.cf-modal__close') ||
        event.target.closest('.cf-modal__backdrop')
      ) {
        this.closeModal();
      }
    }

    openItem(card) {
      if (!card) return;
      const panel = card.parentElement?.querySelector('.cf-hub-panel');
      if (panel?.classList.contains('cf-hub-panel--open')) return;
      this.closePanels();
      panel?.classList.add('cf-hub-panel--open');
      card.setAttribute('aria-expanded', 'true');
      this.bindJotform(panel);
    }

    bindJotform(panel) {
      const iframe = panel?.querySelector('.cf-form__iframe');
      const src = iframe?.dataset.src;
      if (!iframe || !src) return;

      const form = iframe.closest('.cf-form');
      if (iframe.dataset.cfReady === 'true') {
        revealJotform(form);
        return;
      }

      if (iframe.dataset.cfBound === 'true') return;

      iframe.dataset.cfBound = 'true';
      iframe.src = src;
      initJotformHandler(iframe);
      window.setTimeout(() => revealJotform(form), JOTFORM_READY_TIMEOUT);
    }

    syncAllFlows() {
      this.querySelectorAll('.cf-flow').forEach((flow) => this.syncFlow(flow));
    }

    syncFlow(flow) {
      if (this.closest('.cf-drawer--editor')) return;
      const steps = [...flow.querySelectorAll('.cf-flow__item')];
      let previousSelected = true;
      steps.forEach((step) => {
        const locked = !previousSelected;
        this.setLocked(step, locked);
        previousSelected = !locked && Boolean(step.querySelector('input[type="radio"]:checked'));
      });
      const checkout = flow.querySelector('.cf-flow__checkout');
      if (checkout) this.setLocked(checkout, !previousSelected);
      this.syncCheckout(flow);
    }

    syncCheckout(flow) {
      const list = flow.querySelector('.cf-flow__checkout-selections');
      const root = flow.querySelector('.cf-flow__checkout-form') || flow;
      if (!list) return;
      const steps = [...flow.querySelectorAll('.cf-flow__item')];
      const complete =
        steps.length > 0 &&
        steps.every((step) => step.querySelector('input[type="radio"]:checked'));
      const groups = complete ? this.fitGroups(root) : this.sampleFitGroups(flow);
      const fragment = document.createDocumentFragment();
      groups.forEach(({ name, value }) => {
        const item = document.createElement('li');
        item.className = 'cf-type-caption';
        const label = document.createElement('strong');
        label.textContent = name;
        item.append(label, document.createTextNode(`: ${value}`));
        fragment.append(item);
      });
      list.replaceChildren(fragment);
      this.syncPropertyFields(root, complete ? groups : []);
    }

    syncPropertyFields(root, selections) {
      const box = root.querySelector('.cf-flow__checkout-properties');
      if (!box) return;
      const fragment = document.createDocumentFragment();
      selections.forEach(({ name, value }) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${name}]`;
        input.value = value;
        fragment.append(input);
      });
      box.replaceChildren(fragment);
    }

    setLocked(el, locked) {
      el.classList.toggle('is-locked', locked);
      const options = el.querySelector('.cf-flow__options');
      (options || el).toggleAttribute('inert', locked);
    }

    closePanels() {
      this.closeModal();
      this.querySelectorAll('.cf-hub-card').forEach((card) => {
        card.parentElement?.querySelector('.cf-hub-panel')?.classList.remove('cf-hub-panel--open');
        card.setAttribute('aria-expanded', 'false');
      });
    }

    reset() {
      this.closePanels();
    }

    noteProperties(form) {
      const extras = [];
      form?.querySelectorAll('[data-cf-property]').forEach((el) => {
        const name = (el.dataset.cfProperty || '').trim();
        const value = String(el.value || '').trim();
        if (!name || !value) return;
        extras.push({ name, value });
      });
      return extras;
    }

    checkout(form) {
      if (!form || form.dataset.cfSubmitting === 'true') return;
      const button = form.querySelector('.cf-flow__checkout-button');
      if (button?.disabled) return;
      const flow = form.closest('[data-custom-fit="flow"]');
      const steps = [...(flow?.querySelectorAll('.cf-flow__item') || [])];
      const groups = this.fitGroups(form);
      if (!form.querySelector('input[name="id"]')?.value) return;
      if (!steps.length || steps.some((step) => !step.querySelector('input[type="radio"]:checked'))) {
        return;
      }
      this.syncPropertyFields(form, [...groups, ...this.noteProperties(form)]);
      form.dataset.cfSubmitting = 'true';
      if (button) button.disabled = true;

      const formData = new FormData();
      formData.set('id', form.querySelector('input[name="id"]').value);
      formData.set('quantity', '1');
      form.querySelectorAll('.cf-flow__checkout-properties input').forEach((input) => {
        formData.append(input.name, input.value);
      });
      const addUrl =
        form.getAttribute('data-cart-add') ||
        window.theme?.routes?.cart_add_url ||
        '/cart/add';
      fetch(addUrl, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/javascript',
        },
        body: formData,
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            if (response.description) window.alert(response.description);
            throw new Error('Cart add failed');
          }
          const rootUrl = (window.theme?.routes?.root_url || '/').replace(/\/$/, '');
          window.location.assign(`${rootUrl}/checkout`);
        })
        .catch(() => {
          delete form.dataset.cfSubmitting;
          if (button) button.disabled = false;
        });
    }

    sampleFitGroups(flow) {
      if (!this.closest('.cf-drawer--editor')) return [];
      const names = [];
      const seen = {};
      flow?.querySelectorAll('.cf-flow__item').forEach((item) => {
        const name = (item.dataset.fitGroup || '').trim();
        if (!name || seen[name]) return;
        seen[name] = true;
        names.push(name);
      });
      const samples = {
        Jacket: '36, Slim, Short',
        Pants: '32, 30, Slim',
      };
      if (!names.length) {
        return [
          { name: 'Jacket', value: samples.Jacket },
          { name: 'Pants', value: samples.Pants },
        ];
      }
      return names.map((name) => ({
        name,
        value: samples[name] || '36, Short, Classic',
      }));
    }

    fitGroups(root) {
      const flow = root?.closest('[data-custom-fit="flow"]');
      const groups = [];
      const indexByName = {};
      flow?.querySelectorAll('.cf-flow__item').forEach((item) => {
        const checked = item.querySelector('input[type="radio"]:checked');
        if (!checked) return;
        const name = (item.dataset.fitGroup || '').trim();
        const value = (checked.value || '').trim();
        if (!name || !value) return;
        const cartLabel = (item.dataset.cartLabel || '').trim();
        const token = cartLabel ? `${cartLabel} ${value}` : value;
        if (indexByName[name] == null) {
          indexByName[name] = groups.length;
          groups.push({ name, values: [token] });
          return;
        }
        groups[indexByName[name]].values.push(token);
      });
      return groups.map(({ name, values }) => ({
        name,
        label: name,
        value: values.join(', '),
      }));
    }

    fitSelections(root) {
      return this.fitGroups(root);
    }

    fitProperties(root) {
      return Object.fromEntries(this.fitGroups(root).map(({ name, value }) => [name, value]));
    }

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    cancelModalClose() {
      if (this._modalCloseTimer) {
        window.clearTimeout(this._modalCloseTimer);
        this._modalCloseTimer = 0;
      }
      if (!this._onModalClose) return;
      this.modal?.removeEventListener('transitionend', this._onModalClose);
      this._onModalClose = null;
    }

    hideModal() {
      this.cancelModalClose();
      this._modalClosing = false;
      if (!this.modal) return;
      this.modal.classList.remove('cf-modal--open');
      this.modal.hidden = true;
    }

    openModal(item) {
      if (!this.modal) return;
      if (item) {
        const source = item.querySelector('.cf-flow__modal-content');
        const body = this.modal.querySelector('.cf-modal__body');
        const dialog = this.modal.querySelector('.cf-modal__dialog');
        const title = source?.querySelector('.cf-type-title')?.textContent?.trim();
        if (dialog && title) dialog.setAttribute('aria-label', title);
        if (body) {
          body.replaceChildren();
          if (source) body.append(...source.cloneNode(true).childNodes);
        }
      }
      this.cancelModalClose();
      this._modalClosing = false;
      this._modalClickGuard = Date.now() + 500;
      this.modal.hidden = false;
      this.modal.offsetWidth;
      this.modal.classList.add('cf-modal--open');
    }

    closeModal() {
      if (!this.modal || this.modal.hidden || this._modalClosing) return;
      const instant =
        !this.modal.classList.contains('cf-modal--open') ||
        this.prefersReducedMotion() ||
        this.closest('.cf-drawer--editor');
      if (instant) {
        this.hideModal();
        return;
      }
      this.cancelModalClose();
      const dialog = this.modal.querySelector('.cf-modal__dialog');
      if (!dialog) {
        this.hideModal();
        return;
      }
      this._modalClosing = true;
      this._onModalClose = (event) => {
        if (!this._modalClosing || event.target !== dialog) return;
        this.hideModal();
      };
      this.modal.classList.remove('cf-modal--open');
      dialog.addEventListener('transitionend', this._onModalClose);
      this._modalCloseTimer = window.setTimeout(() => this.hideModal(), 500);
    }
  }

  customElements.define('custom-fit-stack', CustomFitStack);
}

if (!customElements.get('custom-fit-trigger')) {
  class CustomFitTrigger extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.button?.addEventListener('click', () => {
        this.dispatchEvent(
          new CustomEvent('custom-fit:open', {
            bubbles: true,
            detail: {
              productId: this.dataset.productId,
              variantId: this.dataset.variantId,
              productHandle: this.dataset.productHandle,
            },
          })
        );
      });
    }
  }

  customElements.define('custom-fit-trigger', CustomFitTrigger);
}
